import React from 'react'
import clsx from 'clsx'

import { CheckCircle } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { FormControlLabel, FormHelperText, Radio } from '@material-ui/core'

import { DefaultRadioGroupProps, DefaultRadioOption, DefaultRadioValue } from './DefaultRadioGroup'

export type DefaultRadioItemProps<TValue extends DefaultRadioValue> =
  Pick<DefaultRadioGroupProps<TValue>,
    'value'
    | 'disabled'
    | 'optionClassName'
    | 'getOptionClassName'
    | 'optionRadioClassName'
    | 'optionLabelClassName'
    | 'getOptionLabelClassName'
    | 'optionHintClassName'>
  & DefaultRadioGroupProps<TValue>['optionProps']
  & {
  option: DefaultRadioOption<TValue>
}

export const DefaultRadioItem =
  <TValue extends DefaultRadioValue>
  ({
     option, value,
     disabled,
     optionClassName, getOptionClassName,
     optionRadioClassName,
     optionLabelClassName, getOptionLabelClassName,
     optionHintClassName,
     ...passProps
   }: DefaultRadioItemProps<TValue>) => {
    const classes = useStyles()

    const checked = value === option.value

    // TODO: This is a radio button, but it looks like a checkbox, see https://www.figma.com/design/111fwVTs0DgwmihMNUh7KR/MP360_2024_2025-Design-Nina?node-id=646-9175&t=iABMNuv62Ts2I2AX-4
    return (
      <FormControlLabel
        value={getOptionKey(option)}
        disabled={disabled || option.disabled}
        className={clsx(
          optionClassName,
          getOptionClassName && getOptionClassName(checked)
        )}
        control={
          <Radio
            checked={checked}
            checkedIcon={<CheckCircle/>}
            disableRipple
            className={clsx(classes.control, optionRadioClassName)}
          />
        }
        label={
          <>
            <span
              className={clsx(
                optionLabelClassName,
                getOptionLabelClassName && getOptionLabelClassName(checked))
              }>
              {option.label}
            </span>

            {
              option.hint &&
              <FormHelperText className={optionHintClassName}>
                {option.hint}
              </FormHelperText>
            }
          </>
        }
        {...passProps}
      />
    )
  }

export const getOptionKey =
  <TValue extends DefaultRadioValue>
  (option: DefaultRadioOption<TValue>): string =>
    JSON.stringify(option.value)

const useStyles = makeStyles(() => ({
  control: {
    '& .MuiTouchRipple-root': {
      color: 'transparent'
    },
    '&:focusVisible': {
      backgroundColor: 'transparent'
    },
    '&:active': {
      backgroundColor: 'transparent'
    },
    '&.MuiRadio-colorSecondary.Mui-checked:hover': {
      backgroundColor: 'transparent'
    },
    '&.MuiIconButton-root:hover': {
      backgroundColor: 'transparent'
    },
    '&.MuiIconButton-colorSecondary:hover': {
      backgroundColor: 'transparent'
    }
  }
}))